import * as React from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Tooltip,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";

/**
 * QuickFilterField
 *
 * Context-free replacement for CEQuickFilter. CEQuickFilter wraps
 * GridToolbarQuickFilter, which needs DataGrid context — so it cannot render in
 * card mode (DataGridResponsive) where no grid is mounted.
 *
 * This field is a plain TextField: the screen owns the value. It is written to
 * work with the same state the desktop quick filter uses, i.e.
 * `filterModel.quickFilterValues`, via
 * `onFilterModelChange({ ...filterModel, quickFilterValues: [value] })`.
 *
 * Props:
 *  - value:    current filter string (e.g. filterModel.quickFilterValues[0])
 *  - onChange: (nextValue: string) => void — debounced (300ms by default)
 *  - iconOnly: renders the 36px search IconButton + Popover variant used in
 *              mobile toolbars; otherwise renders the field inline
 *  - placeholder
 *  - debounceMs (default 300)
 */

const DEFAULT_DEBOUNCE_MS = 300;

const inputSx = (theme) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 36,
    borderRadius: "12px",
    backgroundColor: theme.palette.background.default,
    border: "1px solid transparent",
    borderColor: theme.palette.divider,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.divider,
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
  },
  "& input": {
    color: theme.palette.text.primary,
  },
  "& input::placeholder": {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
  "& .MuiSvgIcon-root": {
    color: theme.palette.text.secondary,
  },
});

export default function QuickFilterField({
  value = "",
  onChange,
  iconOnly = false,
  placeholder = "Search…",
  debounceMs = DEFAULT_DEBOUNCE_MS,
  sx,
  ...props
}) {
  const [inputValue, setInputValue] = React.useState(value ?? "");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const debounceRef = React.useRef(null);
  // Tracks the last value this field pushed out, so an echo of our own change
  // coming back through props does not clobber in-flight typing.
  const pendingValueRef = React.useRef(value ?? "");

  // Sync when the value changes from the outside (clear-all, initial load,
  // desktop quick filter carried over to card mode).
  React.useEffect(() => {
    const nextValue = value ?? "";
    if (nextValue === pendingValueRef.current) return;
    pendingValueRef.current = nextValue;
    setInputValue(nextValue);
  }, [value]);

  // Clear any pending debounce on unmount.
  React.useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const scheduleChange = React.useCallback(
    (nextValue) => {
      pendingValueRef.current = nextValue;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        if (typeof onChange === "function") onChange(nextValue);
      }, debounceMs);
    },
    [debounceMs, onChange],
  );

  const handleInputChange = (event) => {
    const nextValue = event.target.value;
    setInputValue(nextValue);
    scheduleChange(nextValue);
  };

  const handleImmediateChange = React.useCallback(
    (nextValue) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      pendingValueRef.current = nextValue;
      setInputValue(nextValue);
      if (typeof onChange === "function") onChange(nextValue);
    },
    [onChange],
  );

  const hasValue = String(inputValue ?? "").length > 0;

  const field = (
    <TextField
      {...props}
      value={inputValue}
      onChange={handleInputChange}
      placeholder={placeholder}
      variant={props.variant ?? "outlined"}
      size={props.size ?? "small"}
      fullWidth
      autoFocus={iconOnly}
      className={`QuickFilterField ${props.className || ""}`}
      sx={[inputSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchRoundedIcon sx={{ fontSize: 18 }} />
          </InputAdornment>
        ),
        endAdornment: hasValue ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => handleImmediateChange("")}
              edge="end"
              sx={{ p: 0.25 }}
            >
              <ClearRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
    />
  );

  if (!iconOnly) {
    return field;
  }

  return (
    <>
      <Tooltip title="Search">
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          size="small"
          aria-label="Search"
          sx={(theme) => ({
            height: 36,
            width: 36,
            color: hasValue
              ? theme.palette.primary.main
              : theme.palette.grey.main,
            borderColor: hasValue
              ? theme.palette.primary.main
              : theme.palette.divider,
            border: `1px solid ${
              hasValue ? theme.palette.primary.main : theme.palette.divider
            }`,
            borderRadius: "12px",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
          })}
        >
          <SearchRoundedIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: "calc(100vw - 24px)", sm: 300 },
              maxWidth: "calc(100vw - 24px)",
            },
          },
        }}
      >
        <Box sx={{ p: 2 }}>{field}</Box>
      </Popover>
    </>
  );
}
